import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import LoadingScreen from "@/components/LoadingScreen";
import GlassCard from "@/components/GlassCard";
import { Button } from "@/components/ui/button";
import { Clock, LogOut } from "lucide-react";
import Logo from "@/components/Logo";
import { motion } from "framer-motion";

const ProtectedRoute = ({ children }) => {
    const { user, loading, profileChecked, isSetupComplete, isAdmin, signOut } = useAuth();
    const location = useLocation();

    // Still initializing auth — loading screen handles this in App.jsx,
    // but guard here too just in case. Also wait if user is logged in but profile isn't checked yet.
    if (loading || (user && !profileChecked)) {
        return <LoadingScreen />;
    }

    // Not logged in → go to auth
    if (!user) {
        return <Navigate to="/auth" state={{ from: location }} replace />;
    }

    // Lock out non-admin users only if VITE_ENABLE_BETA_LOCK environment flag is explicitly true
    const isBetaLockActive = import.meta.env.VITE_ENABLE_BETA_LOCK === "true";
    if (isBetaLockActive && user && profileChecked && !isAdmin) {
        return (
            <div className="min-h-screen bg-[#fcfcfc] flex items-center justify-center p-6 relative overflow-hidden">
                {/* Animated Background Orbs */}
                <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
                    <motion.div
                        animate={{ scale: [1, 1.2, 1], rotate: [0, 90, 0] }}
                        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                        className="absolute -top-[20%] -left-[10%] w-[60%] aspect-square bg-primary/5 rounded-full blur-[120px]"
                    />
                    <motion.div
                        animate={{ scale: [1.2, 1, 1.2], rotate: [0, -90, 0] }}
                        transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
                        className="absolute -bottom-[20%] -right-[10%] w-[70%] aspect-square bg-[#acc8a2]/10 rounded-full blur-[120px]"
                    />
                </div>

                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.8, ease: [0.23, 1, 0.32, 1] }}
                    className="w-full max-w-sm z-10 text-center"
                >
                    <div className="flex flex-col items-center mb-10">
                        <Logo size="xl" className="mb-2 scale-110" />
                        <p className="text-[12px] tracking-[0.4em] text-muted-foreground font-bold uppercase mt-2">
                            Your Home, Pocket-Sized
                        </p>
                    </div>

                    <GlassCard className="p-8 border-none shadow-[0_20px_50px_rgba(0,0,0,0.05)] rounded-[2.5rem] space-y-6">
                        <div className="w-16 h-16 mx-auto bg-primary/5 rounded-3xl flex items-center justify-center">
                            <Clock className="w-8 h-8 text-primary" />
                        </div>
                        <div className="space-y-2">
                            <h1 className="text-2xl font-extrabold text-foreground tracking-tight">App Coming Soon</h1>
                            <p className="text-sm text-muted-foreground leading-relaxed">
                                Thank you for your interest! We are currently in private beta finalizing the platform. We will open public registration soon.
                            </p>
                        </div>

                        <div className="pt-2">
                            <Button
                                onClick={() => signOut()}
                                variant="outline"
                                className="w-full h-12 rounded-xl font-bold text-sm tracking-wide border-gray-100 hover:bg-gray-50 flex items-center justify-center gap-2"
                            >
                                <LogOut className="w-4 h-4" />
                                Sign Out
                            </Button>
                        </div>
                    </GlassCard>
                </motion.div>
            </div>
        );
    }

    // If setup IS complete and user tries to manually visit setup page, send them home
    if (isSetupComplete && location.pathname === "/setup") {
        return <Navigate to="/" replace />;
    }

    // If setup is NOT complete, force them to the setup page
    if (!isSetupComplete && profileChecked && location.pathname !== "/setup") {
        return <Navigate to="/setup" replace />;
    }

    return <>{children}</>;
};

export default ProtectedRoute;
